import { useState, useEffect, useContext } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { TenantContext } from '../../../../contexts/TenantContext';
import TaskService from '../../../../services/task_service';
import EmployeeService from '../../../../services/employee_service';
import { useMembers } from '../../../../hooks/useMembers';
import { taskSchema, taskDefaults } from '../../../../models/task';

const ACTIVE_STATUSES = new Set(['new', 'scheduled', 'doing', 'waiting', 'overdue']);

export const useCreateTask = (onSuccessCallback, onClose) => {
    const { idTenant, user } = useContext(TenantContext);
    const { members, loading: membersLoading } = useMembers();

    const [employees, setEmployees] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);
    const [memberHistory, setMemberHistory] = useState([]);
    const [memberHistoryLoading, setMemberHistoryLoading] = useState(false);

    // Formik configuration
    const formik = useFormik({
        initialValues: taskDefaults,
        validationSchema: taskSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            if (!idTenant) {
                toast.error("Erro ao identificar o tenant ativo.");
                return;
            }
            try {
                // If the value strings refer to IDs, we need to find the full objects so the converter can denormalize them
                const selectedMemberObj = members.find(m => m.id === values.selectedMember) || null;
                const selectedEmployeesObjs = employees.filter(e => (values.selectedEmployees || []).includes(e.id));

                // Se mais de um colaborador foi selecionado, cria uma tarefa para cada um
                if (selectedEmployeesObjs.length > 1) {
                    const createdTasks = [];
                    for (const emp of selectedEmployeesObjs) {
                        const preparedValues = {
                            ...values,
                            selectedMember: selectedMemberObj,
                            selectedEmployees: [emp]
                        };
                        const newTask = await TaskService.createTask(idTenant, user, preparedValues);
                        createdTasks.push(newTask);
                    }
                    toast.success(`${createdTasks.length} tarefas criadas com sucesso!`);
                    resetForm();
                    if (onSuccessCallback) onSuccessCallback(createdTasks[0]);
                } else {
                    const preparedValues = {
                        ...values,
                        selectedMember: selectedMemberObj,
                        selectedEmployees: selectedEmployeesObjs
                    };
                    const newTask = await TaskService.createTask(idTenant, user, preparedValues);
                    toast.success("Tarefa criada com sucesso!");
                    resetForm();
                    if (onSuccessCallback) onSuccessCallback(newTask);
                }
                if (onClose) onClose();
            } catch (error) {
                console.error("Erro no formulário de tarefa:", error);
                toast.error("Erro ao criar tarefa.");
            } finally {
                setSubmitting(false);
            }
        }
    });

    // Load form select options
    useEffect(() => {
        let isMounted = true;

        const loadResources = async () => {
            if (!idTenant) return;
            setLoadingResources(true);
            try {
                const employeesList = await EmployeeService.getEmployees(idTenant);

                if (isMounted) {
                    setEmployees(employeesList);
                }
            } catch (err) {
                console.error("Erro ao carregar recursos do formulário:", err);
                if (isMounted) toast.error("Não foi possível carregar os clientes e funcionários.");
            } finally {
                if (isMounted) setLoadingResources(false);
            }
        };

        if (idTenant) {
            loadResources();
        }

        return () => { isMounted = false; };
    }, [idTenant]);

    const handleMemberChange = async (option) => {
        formik.setFieldValue('selectedMember', option ? option.value : '');
        setMemberHistory([]);
        if (!option?.value || !idTenant) return;
        setMemberHistoryLoading(true);
        try {
            const tasks = await TaskService.getTasksByMember(idTenant, option.value);
            setMemberHistory(tasks);
        } catch {
            // histórico não é crítico para criação da tarefa
        } finally {
            setMemberHistoryLoading(false);
        }
    };

    const activeTasks = memberHistory.filter(t => ACTIVE_STATUSES.has(t.status));
    const pastTasks   = memberHistory.filter(t => !ACTIVE_STATUSES.has(t.status)).slice(0, 5);

    return {
        formik,
        members,
        employees,
        loadingResources: loadingResources || membersLoading,
        memberHistory: { active: activeTasks, past: pastTasks, loading: memberHistoryLoading },
        handleMemberChange,
    };
};
